'use client';

import {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from 'react';
import type {
	RegisterFormData,
	LoginFormData,
	ForgotPasswordFormData,
	UpdatePasswordFormData,
} from '../validation/schemas';
import type { AuthUser } from '../types/auth.type';
import {
	signIn as signInAction,
	signUp as signUpAction,
	signOut as signOutAction,
	resetPassword as resetPasswordAction,
	updatePassword as updatePasswordAction,
	getCurrentUser,
} from '../actions/auth-actions';
import { organizationStorage } from '@/features/organization/utils/organization-storage';
import { safeFetchQuery, useSafeMutation } from '@/core/query';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '@/core/analytics';
import { logger } from '@/core/logger';

interface IAuthContext {
	user: AuthUser | null;
	displayName: string;
	isLoading: boolean;
	signUp: (data: RegisterFormData) => Promise<void>;
	signIn: (data: LoginFormData) => Promise<void>;
	resetPassword: (data: ForgotPasswordFormData) => Promise<void>;
	updatePassword: (data: UpdatePasswordFormData) => Promise<void>;
	signOut: () => Promise<void>;
	refreshUser: () => Promise<void>;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

export interface AuthProviderProps {
	children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
	const queryClient = useQueryClient();
	const [user, setUser] = useState<AuthUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const analytics = useAnalytics();

	// Get display name from email (part before @) or fallback to "User"
	const displayName = user?.email ? user.email.split('@')[0] : 'User';

	// Mutations for auth operations
	const { mutateAsync: signUpMutation } = useSafeMutation({
		action: (data: { email: string; password: string }) =>
			signUpAction(data.email, data.password),
	});

	const { mutateAsync: signInMutation } = useSafeMutation({
		action: (data: { email: string; password: string }) =>
			signInAction(data.email, data.password),
	});

	const { mutateAsync: resetPasswordMutation } = useSafeMutation({
		action: (email: string) => resetPasswordAction(email),
	});

	const { mutateAsync: updatePasswordMutation } = useSafeMutation({
		action: (password: string) => updatePasswordAction(password),
	});

	const { mutateAsync: signOutMutation } = useSafeMutation({
		action: () => signOutAction(),
	});

	// Load user from server on mount
	useEffect(() => {
		refreshUser();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const refreshUser = async (): Promise<void> => {
		try {
			setIsLoading(true);
			const userData = await safeFetchQuery(queryClient, {
				queryKey: ['current-user'],
				action: () => getCurrentUser(),
			});
			setUser(userData);

			// Identify user in analytics services
			if (userData?.id) {
				analytics.identify(userData.id, {
					email: userData.email,
				});
			}
		} catch (error) {
			logger.warn('Failed to refresh user', error);
			setUser(null);
		} finally {
			setIsLoading(false);
		}
	};

	const signUp = async (data: RegisterFormData): Promise<void> => {
		await signUpMutation({ email: data.email, password: data.password });
		analytics.logEvent('user_signed_up');
	};

	const signIn = async (data: LoginFormData): Promise<void> => {
		await signInMutation({ email: data.email, password: data.password });
		analytics.logEvent('user_logged_in');
		window.location.href = '/dashboard';
	};

	const resetPassword = async (
		data: ForgotPasswordFormData
	): Promise<void> => {
		await resetPasswordMutation(data.email);
	};

	const updatePassword = async (
		data: UpdatePasswordFormData
	): Promise<void> => {
		await updatePasswordMutation(data.password);
	};

	const signOut = async (): Promise<void> => {
		await signOutMutation();

		// Clear all cached data
		organizationStorage.clear();

		setUser(null);
		window.location.href = '/';
	};

	const value: IAuthContext = {
		user,
		displayName,
		isLoading,
		signUp,
		signIn,
		resetPassword,
		updatePassword,
		signOut,
		refreshUser,
	};

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth(): IAuthContext {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}

export default AuthContext;
